import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';
import { Modal } from '../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../CommonComponents/OPL/Modal/ModalHeader';
import { Button } from '../../../CommonComponents/OPL/Button/Button';
import { Alert } from '../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Textarea } from '../../../CommonComponents/OPL/Textarea/Textarea';
import { Select } from '../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';
import { Loading } from '../../../CommonComponents/OPL/Loading/Loading';
import { DATE_FORMAT } from '../../../CommonComponents/OPL/dates/utils';
import { useCreateUserActivityMutation } from '../../api/useCreateUserActivityMutation';
import { queryKeys } from '../../api/queryKeys';
import { ActivityType, CreateUserActivityDto } from '../../UserActivity.meta';
import { ActivityDateTimePicker } from '../ActivityDateTimePicker/ActivityDateTimePicker';
import {
  ActivityDateTime,
  buildActivityDateIso,
  getNowAmPm,
  getNowHours,
  getNowMinutes,
} from '../ActivityDateTimePicker/ActivityDateTimePicker.meta';
import { ConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import {
  activityTypeOptions,
  MAX_ACTIVITY_NAME_CHARS,
  MAX_COMMENT_CHARS,
  validation,
} from './AddUserActivity.meta';

interface FormValues {
  date: string;
  activityType: ActivityType;
  activityName: string;
  comment: string;
}

interface Props {
  clientId: string;
  hideAutomatic: boolean;
  isDataFiltered: boolean;
  onClose(): void;
  resetFilters(): void;
}

export const AddUserActivity = ({
  clientId,
  hideAutomatic,
  isDataFiltered,
  onClose,
  resetFilters,
}: Props) => {
  const [newActivity, setNewActivity] = useState<FormValues | null>(null);
  const queryClient = useQueryClient();

  const { mutateAsync: createUserActivity, isLoading, error } = useCreateUserActivityMutation();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      date: '',
      activityType: ActivityType.DeliveryOfPlan,
      activityName: '',
      comment: '',
    },
    resolver: yupResolver(validation),
  });

  const activityType = watch('activityType');
  const comment = watch('comment');

  const [activityDateTimeState, setActivityDateTimeInternal] = useState<ActivityDateTime>({
    date: moment().format(DATE_FORMAT),
    hours: getNowHours(),
    minutes: getNowMinutes(),
    amPm: getNowAmPm(),
  });

  const setActivityDateTime = useCallback(
    (data: ActivityDateTime) => {
      const iso = buildActivityDateIso(data);
      setValue('date', iso, { shouldValidate: true, shouldDirty: true });
      setActivityDateTimeInternal(data);
    },
    [setValue],
  );

  useEffect(() => {
    const iso = buildActivityDateIso(activityDateTimeState);
    setValue('date', iso, { shouldValidate: true, shouldDirty: false });
  }, []);

  return (
    <>
      <Modal
        isOpen
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={false}
        aria={{ labelledby: 'add-user-activity-modal-title' }}
      >
        <ModalHeader as="h2" id="add-user-activity-modal-title">
          Add Activity
        </ModalHeader>
        <ModalCloseButton onClick={onClose} />

        <form onSubmit={handleSubmit(values => setNewActivity(values))}>
          <ModalBody>
            <Box display="flex" flexDirection="column" gridGap="24">
              {error && error.message && <Alert variant="error" message={error.message} />}

              {isLoading && <Loading />}

              <Box>
                <ActivityDateTimePicker
                  value={activityDateTimeState}
                  setActivityDateTime={setActivityDateTime}
                />
                {errors.date?.message && (
                  <HelperText variant="error" mt="8">
                    {errors.date.message}
                  </HelperText>
                )}
              </Box>

              <Controller
                name="activityType"
                control={control}
                render={({ field: { value, onChange, ref } }) => (
                  <Select
                    innerRef={ref}
                    label="Activity Type"
                    value={activityTypeOptions.find(option => option.value === value)}
                    options={activityTypeOptions}
                    onChange={option => onChange(option?.value ?? ActivityType.DeliveryOfPlan)}
                    isPortal
                  />
                )}
              />

              {activityType === ActivityType.OtherActivity && (
                <Input
                  label="Name of the Activity"
                  maxLength={MAX_ACTIVITY_NAME_CHARS}
                  {...register('activityName')}
                  formVariant={errors.activityName ? 'error' : undefined}
                  helperText={errors.activityName?.message}
                />
              )}

              <Box>
                <Textarea
                  label="Comment"
                  placeholder="Optional"
                  maxLength={MAX_COMMENT_CHARS}
                  rows={5}
                  {...register('comment')}
                  formVariant={errors.comment ? 'error' : undefined}
                  helperText={errors.comment?.message}
                />
                <Text
                  fontSize="small"
                  color="neutral.600"
                  mt="8"
                  css={{ textAlign: 'right', display: 'block' }}
                >
                  {MAX_COMMENT_CHARS - (comment?.length ?? 0)} characters remaining
                </Text>
              </Box>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="subtleInactive" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" ml="8">
              Add Activity
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {!!newActivity && (
        <ConfirmationModal
          isOpen
          onClose={() => setNewActivity(null)}
          title="Please note, once this activity is added, the activity cannot be edited or deleted."
          message="By clicking Confirm, you acknowledge this activity is correct and as intended."
          footer={
            <>
              <Button variant="subtleInactive" onClick={() => setNewActivity(null)}>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    const { activityName, activityType: at, date, comment: c } = newActivity;

                    const activity: CreateUserActivityDto = {
                      ActivityName: activityName,
                      ActivityType: at,
                      Date: date,
                      Comment: c,
                    };

                    setNewActivity(null);

                    await createUserActivity({ clientId, activity, hideAutomatic });
                    await queryClient.invalidateQueries(
                      queryKeys.activityFilterOptions(clientId, hideAutomatic),
                    );
                    if (isDataFiltered) {
                      resetFilters();
                    } else {
                      await queryClient.invalidateQueries(
                        queryKeys.activityLogsForClient(clientId),
                      );
                    }
                    onClose();
                  } catch {
                    // Ignore
                  }
                }}
              >
                Confirm
              </Button>
            </>
          }
        />
      )}
    </>
  );
};
